<template>
  <div :data-id="dataId" class="flex-shrink-0 bl-icon-component">
    <div
      :data-id="dataId"
      class="bl-component-svg"
      ref="blComponent"
      :class="
        `${color ? `bl-color-${color}` : ''} ${clickable ? 'bl-clickable' : ''}`
      "
      @click="click"
    ></div>
    <div
      v-if="tooltip"
      class="bl-icon-tooltip"
      :class="[tooltipPosition ? `bl-icon-tooltip-${tooltipPosition}` : '']"
    >
      <BlTextBody3 class="bl-icon-tooltip-text" :text="tooltip" />
    </div>
  </div>
</template>

<script>
import BlTextBody3 from "./BlTextBody3.vue";

export default {
  name: "BlIcon",
  components: {
    BlTextBody3
  },
  props: {
    dataId: { type: String, default: "" },
    icon: { type: String, required: true },
    /**
     * The color of icons v1    @value: primary, secondary, success, success-4, error, warning, info, pending, pending-4, white
     * The color of icons v2    @value: slate-6, slate-8, orange-6, lime-6, lime-7, rose-7, tulip-6, tulip-7, cornflower-8, tomato-7, grass-6, teal-7
     * The prompt colors        @value: cherry, blush, gold, daffodil, lake, mint, sky, hydrangeas, grey
     * The prompt colors state  @value: prompt-idle, prompt-active
     */
    color: { type: String, default: "primary" },
    clickable: { type: Boolean, default: false },
    tooltip: { type: String, required: false },
    tooltipPosition: { type: String, default: "bottom" }
  },
  data() {
    return {
      blElementText: "",
      iconKey: "icon-key"
    };
  },
  computed: {
    url() {
      if (!window || !window.location) return "";
      return `${window.location.origin}/static/icons`;
    }
  },
  watch: {
    blElementText(val) {
      let svg = val;
      if (this.dataId) {
        svg = val.replaceAll("<svg", `<svg data-id="${this.dataId}"`);
      }
      this.$refs.blComponent.innerHTML = svg;
    },
    async icon() {
      await this.loadElement();
    }
  },
  mounted() {
    this.loadElement();
  },
  methods: {
    async loadElement() {
      try {
        const data = await fetch(`${this.url}/${this.icon}.svg`);
        this.blElementText = await data.text();
        return true;
      } catch (e) {
        return false;
      }
    },
    click() {
      if (this.clickable) this.$emit("clicked");
    }
  }
};
</script>

<style lang="scss">
.bl-icon-component {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: 0.25s transform;
  .bl-component-svg {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: 0.25s transform;
    * {
      pointer-events: none;
    }
    svg {
      position: relative;
      width: 100%;
      height: 100%;
      &.bl-svg-filled {
        .bl-svg-filled-main {
          fill: var(--font-primary-color);
        }
        .bl-svg-filled-secondary {
          fill: var(--secondary-color);
        }
      }
      &.bl-svg-outlined {
        .bl-svg-outlined-main {
          stroke: var(--font-primary-color);
        }
        .bl-svg-outlined-secondary {
          stroke: var(--secondary-color);
        }
      }
    }
    // v1
    &.bl-color-primary {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--primary-color);
          }
          .bl-svg-filled-primary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--primary-color);
          }
          .bl-svg-outlined-primary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-secondary {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--secondary-color);
          }
          .bl-svg-filled-secondary {
            fill: #fff;
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--secondary-color);
          }
          .bl-svg-outlined-secondary {
            stroke: #fff;
          }
        }
      }
    }
    &.bl-color-success {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--success-color);
          }
          .bl-svg-filled-secondary {
            fill: #fff;
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--success-color);
          }
          .bl-svg-outlined-secondary {
            stroke: #fff;
          }
        }
      }
    }
    &.bl-color-success-4 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--success-four);
          }
          .bl-svg-filled-secondary {
            fill: #fff;
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--success-four);
          }
          .bl-svg-outlined-secondary {
            stroke: #fff;
          }
        }
      }
    }
    &.bl-color-error {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--error-color);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--error-color);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-warning {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--warning-color);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--warning-color);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-info {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--info-five);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--info-five);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-pending {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--pending-color);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--pending-color);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-pending-4 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--pending-four);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--pending-four);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-white {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: #fff;
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: #fff;
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    // v2
    &.bl-color-slate-6 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Slate-6, #98a2b3);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Slate-6, #98a2b3);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-slate-8 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Slate-8, #475467);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Slate-8, #475467);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-orange-6 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Orange-6, #ec4a0a);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Orange-6, #ec4a0a);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-lime-6 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Lime-6, #9bbe24);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Lime-6, #9bbe24);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-lime-7 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Lime-7, #789719);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Lime-7, #789719);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-rose-7 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Rose-7, #f63d68);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Rose-7, #f63d68);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-tulip-6 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Tulip-6, #e8b100);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Tulip-6, #e8b100);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-tulip-7 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Tulip-7, #d18d00);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Tulip-7, #d18d00);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-cornflower-8 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Cornflower-8, #0172a3);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Cornflower-8, #0172a3);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-tomato-7 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Tomato-7, #dd3f30);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Tomato-7, #dd3f30);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-grass-6 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Grass-6, #71c115);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Grass-6, #71c115);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    &.bl-color-teal-7 {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--Teal-7, #1d847f);
          }
          .bl-svg-filled-secondary {
            fill: var(--secondary-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--Teal-7, #1d847f);
          }
          .bl-svg-outlined-secondary {
            stroke: var(--secondary-color);
          }
        }
      }
    }
    // prompt color
    &.bl-color-cherry {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--cherry-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--cherry-color);
          }
        }
      }
    }
    &.bl-color-blush {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--blush-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--cherry-color);
          }
        }
      }
    }
    &.bl-color-gold {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--gold-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--gold-color);
          }
        }
      }
    }
    &.bl-color-daffodil {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--daffodil-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--gold-color);
          }
        }
      }
    }
    &.bl-color-lake {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--lake-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--lake-color);
          }
        }
      }
    }
    &.bl-color-mint {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--mint-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--lake-color);
          }
        }
      }
    }
    &.bl-color-sky {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--sky-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--sky-color);
          }
        }
      }
    }
    &.bl-color-hydrangeas {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--hydrangeas-color);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--sky-color);
          }
        }
      }
    }
    &.bl-color-grey {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--grey-two);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--grey-three);
          }
        }
      }
    }
    // prompt color state
    &.bl-color-prompt-idle {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--light-purple-one);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: transparent;
          }
        }
      }
    }
    &.bl-color-prompt-active {
      svg {
        &.bl-svg-filled {
          .bl-svg-filled-main {
            fill: var(--light-purple-one);
          }
        }
        &.bl-svg-outlined {
          .bl-svg-outlined-main {
            stroke: var(--light-purple-five);
          }
        }
      }
    }
    //
    &.bl-clickable {
      cursor: pointer;
    }
  }
  .bl-icon-tooltip {
    pointer-events: none;
    opacity: 0;
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 50%;
    width: 8.375rem;
    padding: 0.75rem 1rem;
    box-sizing: border-box;
    background: var(--Dark-Purple-2, #45356d);
    transform: translateX(-50%);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99;
    border-radius: 5px;

    .bl-icon-tooltip-text {
      color: #fff;
      text-align: center;
    }

    &::before {
      content: "";
      position: absolute;
      bottom: 99%;
      left: 50%;
      margin-left: -0.5rem;
      border-width: 0.5rem;
      border-style: solid;
      border-color: transparent transparent var(--Dark-Purple-2, #45356d)
        transparent;
    }
  }

  .bl-icon-tooltip-left {
    top: 50%;
    left: -10rem;
    transform: translateY(-50%);
    margin-right: 0.5rem;

    &::after {
      content: "";
      position: absolute;
      top: 50%;
      right: -1rem;
      transform: translateY(-50%) rotate(90deg);
      border-width: 0.5rem;
      border-style: solid;
      border-color: transparent transparent var(--Dark-Purple-2, #45356d)
        transparent;
    }

    &::before {
      display: none;
    }
  }
  .bl-icon-tooltip-bottom-left {
    top: calc(100% + 0.1rem);
    left: -8rem;
    transform: translateX(0);
    margin-right: 0.5rem;
    border-radius: 10px 0px 10px 10px;

    &::before {
      display: none;
    }
  }

  &:hover {
    .bl-icon-tooltip {
      opacity: 1;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BlIcon.vue